import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import '../styles/SubmitLead.css';

export default function SubmitLead() {
  const mobile = useMobile();
  const navigate = useNavigate();
  const [brokerInfo, setBrokerInfo] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: '',
    gdpr_consent: false
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [leadType, setLeadType] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [urgency, setUrgency] = useState('');
  const [creditIssues, setCreditIssues] = useState(false);

  const LEAD_TYPES = ['Purchase', 'Remortgage', 'First-time buyer', 'Buy-to-let'];
  const EMPLOYMENT_STATUSES = ['Employed', 'Self-employed', 'Retired', 'Other'];
  const URGENCY_OPTIONS = ['Urgent', 'Standard', 'No rush'];

  useEffect(() => {
    loadBrokerInfo();
  }, []);

  const loadBrokerInfo = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr || '{}');
      const token = localStorage.getItem('token');

      const brokerCompanyId = user.brokerCompanyId;
      if (!brokerCompanyId) {
        throw new Error("Your account isn't linked to a broker yet. Please contact your broker so they can fix this.");
      }

      const brokerRes = await fetch(`/api/companies/${brokerCompanyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!brokerRes.ok) {
        const errorData = await brokerRes.json();
        console.log('Broker error:', errorData);
        throw new Error('Failed to load broker info');
      }

      const brokerData = await brokerRes.json();
      setBrokerInfo(brokerData.company);
    } catch (err) {
      console.error('Full error:', err);
      setError('Failed to load broker information: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.gdpr_consent) {
      setError('Please confirm GDPR consent');
      return;
    }

    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/introducer-leads', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_name: formData.client_name,
          client_email: formData.client_email,
          client_phone: formData.client_phone,
          mortgage_type: leadType || undefined,
          employment_status: employmentStatus || undefined,
          urgency: urgency || undefined,
          credit_issues: creditIssues,
          notes: formData.notes || undefined,
        })
      });

      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to submit lead');

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Failed to submit lead');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Sidebar />
        <div style={{ marginLeft: mobile ? 0 : 70, padding: '24px' }}>Loading...</div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: mobile ? 0 : 70, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <div className="submit-lead-page">

          {/* ── Header ────────────────────────────────────────────────── */}
          <header className="sl-header">
            <div className="sl-header-inner">
              <button
                className="sl-back-btn"
                onClick={() => navigate('/introducer-dashboard')}
                aria-label="Back to dashboard"
              >
                ←
              </button>
              <div>
                <h1 className="sl-title">Submit a Lead</h1>
                {!mobile && <p className="sl-subtitle">Send a new client introduction to your broker</p>}
              </div>
            </div>
          </header>

          {/* ── Content ───────────────────────────────────────────────── */}
          <div className="sl-content">
            {submitted ? (
              <div className="sl-success-card">
                <div className="sl-success-icon">📧</div>
                <h2 className="sl-success-title">
                  Verification email sent to {formData.client_name.split(' ')[0]}
                </h2>
                <p className="sl-success-body">
                  Before this lead reaches {brokerInfo?.name || 'your broker'}, your client needs to verify their enquiry.
                  We've sent them an email — here's what happens next:
                </p>
                <div style={{ background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: 13, color: '#9d174d', lineHeight: 1.6 }}>
                  <strong>📌 Important:</strong> Please let {formData.client_name.split(' ')[0]} know to expect a verification email from us — they may need to check their spam folder.
                </div>
                <div className="sl-next-steps">
                  {[
                    ['📧', `A verification email has been sent to ${formData.client_email}. Your client clicks to confirm.`],
                    ['📋', `Once confirmed, ${brokerInfo?.name || 'your broker'} reviews the lead — you'll get an email straight away.`],
                    ['💷', 'Track every stage — and your expected commission — from your dashboard.'],
                  ].map(([icon, text], i) => (
                    <div key={i} className="sl-next-step">
                      <span className="sl-step-icon">{icon}</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
                <div className="sl-success-actions">
                  <button className="sl-btn-secondary" onClick={() => {
                    setFormData({ client_name: '', client_email: '', client_phone: '', notes: '', gdpr_consent: false });
                    setLeadType('');
                    setEmploymentStatus('');
                    setUrgency('');
                    setCreditIssues(false);
                    setSubmitted(false);
                  }}>
                    Submit another lead
                  </button>
                  <button className="sl-btn-primary" onClick={() => navigate('/introducer-dashboard')}>
                    Go to dashboard →
                  </button>
                </div>
              </div>
            ) : (
              <div className="sl-form-card">

                {/* ── Broker contact card ─────────────────────────────── */}
                {brokerInfo && (
                  <div className="sl-broker-card">
                    {/* Logo + name row */}
                    <div className="sl-broker-identity">
                      <div className="sl-broker-logo">
                        {brokerInfo.branding?.logo_url
                          ? <img src={brokerInfo.branding.logo_url} alt={brokerInfo.name} />
                          : '🏛️'}
                      </div>
                      <div>
                        <div className="sl-broker-name">{brokerInfo.name}</div>
                        {brokerInfo.branding?.company_bio && (
                          <div className="sl-broker-bio">{brokerInfo.branding.company_bio}</div>
                        )}
                      </div>
                    </div>

                    {/* Contact chips */}
                    <div className="sl-broker-contacts">
                      {brokerInfo.contact?.phone && (
                        <a href={`tel:${brokerInfo.contact.phone}`} className="sl-contact-chip sl-contact-phone">
                          <span className="sl-chip-icon">📞</span>
                          <span className="sl-chip-label">{brokerInfo.contact.phone}</span>
                        </a>
                      )}
                      {brokerInfo.contact?.email && (
                        <a href={`mailto:${brokerInfo.contact.email}`} className="sl-contact-chip sl-contact-email">
                          <span className="sl-chip-icon">✉️</span>
                          <span className="sl-chip-label">{brokerInfo.contact.email}</span>
                        </a>
                      )}
                      {brokerInfo.website && (
                        <a href={brokerInfo.website} target="_blank" rel="noopener noreferrer" className="sl-contact-chip sl-contact-web">
                          <span className="sl-chip-icon">🌐</span>
                          <span className="sl-chip-label">{brokerInfo.website.replace(/^https?:\/\//, '')}</span>
                        </a>
                      )}
                    </div>

                    {/* Adviser name if set */}
                    {brokerInfo.contact?.name && (
                      <div className="sl-broker-adviser">Your adviser: <strong>{brokerInfo.contact.name}</strong></div>
                    )}
                  </div>
                )}

                {error && <div className="sl-error">{error}</div>}
                {success && <div className="sl-success-msg">{success}</div>}

                <form onSubmit={handleSubmit}>
                  <h3 className="sl-section-title">Client Details</h3>

                  <div className="sl-field">
                    <label>Client Name *</label>
                    <input
                      type="text"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleChange}
                      placeholder="Full name"
                      required
                    />
                  </div>

                  <div className="sl-field">
                    <label>Client Email *</label>
                    <input
                      type="email"
                      name="client_email"
                      value={formData.client_email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                    />
                  </div>

                  <div className="sl-field">
                    <label>Client Phone *</label>
                    <input
                      type="tel"
                      name="client_phone"
                      value={formData.client_phone}
                      onChange={handleChange}
                      placeholder="07xxx xxxxxx"
                      required
                    />
                  </div>

                  <div className="sl-field">
                    <label>Lead type <span className="sl-optional">(optional)</span></label>
                    <div className="sl-chips">
                      {LEAD_TYPES.map(t => (
                        <button key={t} type="button"
                          className={`sl-type-chip${leadType === t ? ' active' : ''}`}
                          onClick={() => setLeadType(leadType === t ? '' : t)}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sl-field">
                    <label>Employment status <span className="sl-optional">(optional)</span></label>
                    <div className="sl-chips">
                      {EMPLOYMENT_STATUSES.map(s => (
                        <button key={s} type="button"
                          className={`sl-type-chip${employmentStatus === s ? ' active' : ''}`}
                          onClick={() => setEmploymentStatus(employmentStatus === s ? '' : s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sl-field">
                    <label>Urgency <span className="sl-optional">(optional)</span></label>
                    <div className="sl-chips">
                      {URGENCY_OPTIONS.map(u => (
                        <button key={u} type="button"
                          className={`sl-type-chip${urgency === u ? ' active' : ''}`}
                          onClick={() => setUrgency(urgency === u ? '' : u)}>
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sl-field">
                    <label className="sl-gdpr-label" style={{ fontWeight: 500, color: '#2d3748', marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={creditIssues}
                        onChange={e => setCreditIssues(e.target.checked)}
                      />
                      <span>Client has credit issues <span className="sl-optional">(optional)</span></span>
                    </label>
                  </div>

                  <div className="sl-field">
                    <label>Notes <span className="sl-optional">(optional)</span></label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any additional information..."
                      rows="3"
                    />
                  </div>

                  <div className="sl-gdpr">
                    <label className="sl-gdpr-label">
                      <input
                        type="checkbox"
                        name="gdpr_consent"
                        checked={formData.gdpr_consent}
                        onChange={handleChange}
                      />
                      <span>I confirm my client is happy to send their details to <strong>{brokerInfo?.name || 'the broker'}</strong> for mortgage enquiry purposes.</span>
                    </label>
                  </div>

                  <div className="sl-actions">
                    <button type="submit" disabled={submitting} className="sl-btn-primary">
                      {submitting ? 'Submitting…' : 'Submit Lead'}
                    </button>
                    <button type="button" onClick={() => navigate('/introducer-dashboard')} className="sl-btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
